import { useState, useEffect } from "react"
import { useData } from "@/lib/data-context"
import { useAuth } from "@/lib/auth-context"
import { getMonthDays, formatDate, getMonthName, groupDaysByWeek, isToday } from "@/lib/date-utils"
import { Button } from "@/components/ui/button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"
import type { WorkStatus } from "@/lib/types"
import { greekHolidays } from "@/lib/holidays"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ENABLE_CHRISTMAS_THEME, ENABLE_VACATION_THEME } from "@/config/theme"
import { VacationEffects } from "@/components/vacation-effects"
import { VacationSunDialog } from "@/components/vacation-sun-dialog"

/**
 * EmployeeCalendar Component
 *
 * Allows authenticated employees to manage their work schedule.
 * Users can cycle through three states: Office → Remote → Day Off → Office
 */
export function EmployeeCalendar() {
  const { user } = useAuth()
  const { employees, setWorkStatus, getWorkStatus, getEmployeeStats, refreshWorkDays, refreshEmployees } = useData()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [sunDialogOpen, setSunDialogOpen] = useState(false)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Fetch employees once on mount
  useEffect(() => {
    refreshEmployees()
  }, [refreshEmployees])

  // Fetch work days when month changes
  useEffect(() => {
    if (!user) return
    refreshWorkDays(year, month)
  }, [user, year, month, refreshWorkDays])

  if (!user) {
    return null
  }

  const monthDays = getMonthDays(year, month)
  const weeks = groupDaysByWeek(monthDays)

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  /**
   * Cycles through work statuses: Office → Remote → Day Off → Office
   */
  const cycleWorkStatus = async (date: Date) => {
    const dateStr = formatDate(date)
    const currentStatus = getWorkStatus(user.id, dateStr)
    
    // Define the cycle
    const statusCycle: WorkStatus[] = ['office', 'remote', 'day_off']
    const currentIndex = statusCycle.indexOf(currentStatus)
    const nextStatus = statusCycle[(currentIndex + 1) % statusCycle.length]
    
    await setWorkStatus(user.id, dateStr, nextStatus)
  }

  /**
   * Gets colleagues who are remote or have day off for a specific date
   * Excludes the current user
   */
  const getColleaguesByStatus = (date: Date) => {
    const dateStr = formatDate(date)
    
    const colleagues: {
      remote: typeof employees
      day_off: typeof employees
    } = {
      remote: [],
      day_off: []
    }

    employees.forEach((emp) => {
      // Skip current user
      if (emp.id === user.id) return
      
      const status = getWorkStatus(emp.id, dateStr)
      
      if (status === 'remote') {
        colleagues.remote.push(emp)
      } else if (status === 'day_off') {
        colleagues.day_off.push(emp)
      }
    })

    return colleagues
  }

  const stats = getEmployeeStats(user.id, year, month)

  const weekDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]

  // Generate snowflakes (only if Christmas theme is enabled)
  const snowflakes = ENABLE_CHRISTMAS_THEME
    ? Array.from({ length: 50 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        animationDuration: Math.random() * 3 + 2 + 's',
        animationDelay: Math.random() * 5 + 's',
        fontSize: Math.random() * 10 + 10 + 'px',
      }))
    : []

  return (
    <div className={cn(
      "min-h-screen relative",
      ENABLE_CHRISTMAS_THEME && "christmas-bg",
      ENABLE_VACATION_THEME && "vacation-bg"
    )}>
      {/* Snowflakes */}
      {ENABLE_CHRISTMAS_THEME && snowflakes.map((snowflake) => (
        <div
          key={snowflake.id}
          className="snowflake"
          style={{
            left: `${snowflake.left}%`,
            animationDuration: snowflake.animationDuration,
            animationDelay: snowflake.animationDelay,
            fontSize: snowflake.fontSize,
          }}
        />
      ))}

      {ENABLE_VACATION_THEME && (
        <VacationEffects
          sunInteractive
          onSunClick={() => setSunDialogOpen(true)}
        />
      )}

      {ENABLE_VACATION_THEME && (
        <VacationSunDialog open={sunDialogOpen} onOpenChange={setSunDialogOpen} />
      )}
      
      <div className="container mx-auto p-4 max-w-5xl relative z-10">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="text-center">
          <h1 className={cn(
            "text-2xl font-bold",
            ENABLE_CHRISTMAS_THEME && "text-white drop-shadow-lg",
            ENABLE_VACATION_THEME && "text-sky-950 drop-shadow"
          )}>
            {ENABLE_CHRISTMAS_THEME
              ? `🎄 ${getMonthName(month)} ${year} 🎄`
              : ENABLE_VACATION_THEME
                ? `🏖️ ${getMonthName(month)} ${year} ☀️`
                : `${getMonthName(month)} ${year}`}
          </h1>
          <div className={cn(
            "flex gap-4 justify-center mt-2 text-sm",
            ENABLE_CHRISTMAS_THEME
              ? "text-white/90 drop-shadow"
              : ENABLE_VACATION_THEME
                ? "text-sky-900/85"
                : "text-muted-foreground"
          )}>
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full bg-blue-500"></span>
              {stats.remote} remote
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full bg-orange-500"></span>
              {stats.dayOff} day{stats.dayOff === 1 ? "" : "s"} off
            </span>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={goToNextMonth}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Calendar header with day names */}
        <div className="grid grid-cols-5 gap-2">
          {weekDays.map((day) => (
            <div key={day} className={cn(
              "text-center font-semibold text-sm py-2",
              ENABLE_CHRISTMAS_THEME
                ? "text-white/90 drop-shadow"
                : ENABLE_VACATION_THEME
                  ? "text-sky-900/85"
                  : "text-muted-foreground"
            )}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar weeks */}
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-5 gap-2">
            {week.map((day, dayIndex) => {
              // Handle empty cells (days outside the month or week)
              if (!day) {
                return <div key={`empty-${weekIndex}-${dayIndex}`} className="min-h-[180px]" />
              }
              
              const dateStr = formatDate(day)
              const status = getWorkStatus(user.id, dateStr)
              const dayOfMonth = day.getUTCDate()
              const colleagues = getColleaguesByStatus(day)
              const today = isToday(day)
              const holiday = greekHolidays[dateStr]

              // Define styling for each status
              const statusStyles = {
                office: {
                  bg: (ENABLE_CHRISTMAS_THEME || ENABLE_VACATION_THEME) ? 'bg-white/90 dark:bg-gray-800/90' : 'bg-card',
                  border: ENABLE_VACATION_THEME
                    ? 'border-amber-200 dark:border-amber-700'
                    : ENABLE_CHRISTMAS_THEME
                      ? 'border-gray-300 dark:border-gray-600'
                      : 'border-border',
                  text: 'text-foreground',
                  label: ENABLE_CHRISTMAS_THEME
                    ? '🏢 Office'
                    : ENABLE_VACATION_THEME
                      ? '🏢 Office'
                      : 'Office',
                  hover: 'hover:border-muted-foreground/50'
                },
                remote: {
                  bg: ENABLE_VACATION_THEME ? 'bg-sky-500' : 'bg-blue-500',
                  border: ENABLE_VACATION_THEME ? 'border-sky-600' : 'border-blue-600',
                  text: 'text-white',
                  label: ENABLE_CHRISTMAS_THEME
                    ? '❄️ Remote'
                    : ENABLE_VACATION_THEME
                      ? '🏖️ Remote'
                      : 'Remote',
                  hover: ENABLE_VACATION_THEME ? 'hover:bg-sky-600' : 'hover:bg-blue-600'
                },
                day_off: {
                  bg: ENABLE_VACATION_THEME ? 'bg-amber-400' : 'bg-orange-500',
                  border: ENABLE_VACATION_THEME ? 'border-amber-500' : 'border-orange-600',
                  text: 'text-white',
                  label: ENABLE_CHRISTMAS_THEME
                    ? '🎁 Day Off'
                    : ENABLE_VACATION_THEME
                      ? '⛱️ Day Off'
                      : 'Day Off',
                  hover: ENABLE_VACATION_THEME ? 'hover:bg-amber-500' : 'hover:bg-orange-600'
                }
              }

              const style = statusStyles[status]

              const dayButton = (
                <button
                  key={dateStr}
                  onClick={() => cycleWorkStatus(day)}
                  disabled={!!holiday}
                  className={cn(
                    "min-h-[180px] p-4 rounded-lg transition-all flex flex-col items-start border-2",
                    "hover:scale-105 active:scale-95 shadow-lg",
                    !holiday && style.bg,
                    !holiday && style.border,
                    !holiday && style.text,
                    !holiday && style.hover,
                    today && (
                      ENABLE_VACATION_THEME
                        ? "ring-4 ring-yellow-300 ring-offset-2 ring-offset-sky-200 shadow-2xl"
                        : "ring-4 ring-yellow-400 ring-offset-2 ring-offset-background shadow-2xl"
                    ),
                    holiday && (
                      ENABLE_CHRISTMAS_THEME
                        ? "bg-green-600 border-green-700 text-white cursor-not-allowed"
                        : ENABLE_VACATION_THEME
                          ? "bg-sky-500 border-sky-600 text-white cursor-not-allowed"
                          : "bg-gray-400 border-gray-500 text-white cursor-not-allowed"
                    )
                  )}
                >
                  {/* User's own status */}
                  <div className="w-full flex flex-col items-center mb-2">
                    <div className="text-2xl font-bold">{dayOfMonth}</div>
                    <div className="text-xs font-medium">{holiday ? holiday : style.label}</div>
                  </div>

                  {/* Colleagues list */}
                  {(colleagues.remote.length > 0 || colleagues.day_off.length > 0) && (
                    <div className="w-full mt-auto max-h-[80px] overflow-y-auto space-y-1">
                      {/* Remote colleagues */}
                      {colleagues.remote.map((colleague) => (
                        <div
                          key={colleague.id}
                          className="text-[10px] px-2 py-1 rounded bg-blue-600/30 border border-blue-400/50 truncate"
                          title={`${colleague.name} - Remote`}
                        >
                          {colleague.name}
                        </div>
                      ))}
                      
                      {/* Day off colleagues */}
                      {colleagues.day_off.map((colleague) => (
                        <div
                          key={colleague.id}
                          className="text-[10px] px-2 py-1 rounded bg-orange-600/30 border border-orange-400/50 truncate"
                          title={`${colleague.name} - Day Off`}
                        >
                          {colleague.name}
                        </div>
                      ))}
                    </div>
                  )}
                </button>
              )

              if (holiday) {
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>{dayButton}</TooltipTrigger>
                    <TooltipContent>
                      <p>{holiday}</p>
                    </TooltipContent>
                  </Tooltip>
                )
              }

              return dayButton
            })}
          </div>
        ))}
      </div>
      </div>
    </div>
  )
}
